import qs from 'query-string';
import jwt_decode from 'jwt-decode';

import { useEffect } from 'react';

export const isAnImageUrl = (str) => {
  if (!str || (typeof str !== 'string' && typeof str !== 'number')) {
    return false;
  }
  const strToTest = typeof str === 'number' ? str.toString() : str;
  return strToTest?.toLowerCase()?.match?.(/\.(jpeg|jpg|gif|png)$/);
};

export const useOutsideClickHandler = (ref, callback) => {
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        callback(event);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [ref, callback]);
};

export const searchUrlChangedEvent = 'newSearchUrl';

export const cleanStrDiacritics = (str) =>
  str.normalize('NFD').replace(/\p{Diacritic}/gu, '');

export const isWindows = () =>
  /^win/i.test(navigator.userAgentData?.platform ?? navigator.platform ?? '');

export const getUrlParams = (name) => {
  const urlParams = new URLSearchParams(window.location.search);

  if (!urlParams) return null;

  const param = urlParams.get(name);
  return param;
};

const paramsToObject = (entries) => {
  const result = {};
  for (const [key, value] of entries) {
    // each 'entry' is a [key, value] tupple
    if (key !== 'jwt') {
      result[key] = Number(value);
    }
  }
  return result;
};

export const getUrlParamsObject = () => {
  const urlParams = new URLSearchParams(window.location.search);
  if (!urlParams) return {};
  return paramsToObject(urlParams);
};

export const getDocumentFromParams = (params = {}) => {
  const { oid, ofid, tid } = params;
  const documentType = oid ? 'opportunity' : ofid ? 'offer' : 'template';
  return {
    id: oid ?? ofid ?? tid,
    type: documentType,
  };
};

/* URL d'édition d'une section, pour l'ouvrir dans un nouvel onglet. Repartir
   de l'URL courante + tid ne suffit pas : oid/ofid y restent prioritaires
   (getDocumentFromParams) et l'onglet rouvrirait le même document, ce qui
   passe le premier en lecture seule (session unique par document). Seul tid
   est gardé ; le jwt est déjà dans le localStorage (getJwt). */
export const sectionEditorUrl = (sectionId) =>
  qs.stringifyUrl({
    url: window.location.origin + window.location.pathname,
    query: { tid: sectionId },
  });

export const updateUrl = (params, { replace = false } = {}) => {
  const search = qs.parse(window.location.search);

  Object.keys(params).forEach((cur) => {
    if (params[cur] === null) {
      delete search[cur];
      delete params[cur];
    }
  });

  /* `origin` seul perdrait le chemin : nettoyer le `jwt` d'une URL
     `/web-editor/123?jwt=…` renverrait l'utilisateur à la racine. Les écrans
     historiques vivent tous à la racine, ce qui masquait le défaut. */
  const url = qs.stringifyUrl({
    url: window.location.origin + window.location.pathname,
    query: { ...search, ...params },
  });
  if (replace) window.history.replaceState({}, null, url);
  else window.history.pushState({}, null, url);
  let searchUrlChangeEvent = new Event(searchUrlChangedEvent);
  window.dispatchEvent(searchUrlChangeEvent);
};

export const emptyUrl = () => {
  const url = qs.stringifyUrl({
    url: window.location.origin,
    query: null,
  });
  window.history.pushState({}, null, url);
};

export const getJwt = () => {
  const jwt = getUrlParams('jwt') ?? window.localStorage.getItem('jwt');

  if (!jwt) return;

  window.localStorage.setItem('jwt', jwt);
  // replace : une nouvelle entrée laisserait le jwt dans la précédente.
  if (getUrlParams('jwt')) updateUrl({ jwt: null }, { replace: true });

  const decoded = jwt_decode(jwt) || {};

  // Nouveau format : { id, roles: [] }
  const roles = Array.isArray(decoded.roles) ? decoded.roles : [];

  const hasSuperadmin = roles.includes('superadmin');
  const hasSetupEditor = roles.includes('setup_editor');
  const hasSetupCosting = roles.includes('setup_costing');
  const hasUserRole = roles.some((r) =>
    ['salesman', 'manager', 'supervisor'].includes(r)
  );

  // Ancien champ éventuel (pour compatibilité si encore présent)
  const legacyRole = decoded.role;

  // Rôle « synthétique » pour la compatibilité avec le code existant
  // - setup_editor seul est vu comme l'ancien setup
  // - setup_editor + rôle utilisateur est vu comme l'ancien admin
  // - superadmin reste superadmin
  let effectiveRole = legacyRole || null;
  if (!effectiveRole) {
    if (hasSuperadmin) {
      effectiveRole = 'superadmin';
    } else if (hasSetupEditor && hasUserRole) {
      effectiveRole = 'admin';
    } else if (hasSetupEditor) {
      effectiveRole = 'setup';
    }
  }

  const isPureSetup =
    effectiveRole === 'setup' &&
    hasSetupEditor &&
    !hasUserRole &&
    !hasSuperadmin;

  // Ancien comportement : un « vrai » setup ne doit pas ouvrir une opportunité (oid)
  if (isPureSetup && getUrlParamsObject().oid) {
    updateUrl({ oid: null });
  }

  const isAdmin =
    hasSuperadmin ||
    hasSetupEditor ||
    hasSetupCosting ||
    legacyRole === 'admin' ||
    legacyRole === 'setup' ||
    legacyRole === 'superadmin';

  return {
    ...decoded,
    roles,
    role: effectiveRole,
    isAdmin,
  };
};

/**
 * L'utilisateur connecté tel que rendu par `GET /api/user`, ou null tant qu'il
 * n'est pas chargé.
 *
 * `require` paresseux : `store/store.js` importe `getJwt` d'ici et appelle
 * getJwt() à l'évaluation du module, un import statique créerait un cycle.
 */
const getStoreUser = () => {
  try {
    return require('../store/store').useStore.getState().user ?? null;
  } catch (e) {
    return null;
  }
};

/**
 * Décisions d'accès calculées par le back, rendues par `GET /api/user` :
 * `{ web_editor, web_admin, manage_general_rules }`. null tant que la
 * réponse n'est pas arrivée.
 */
export const getUserPermissions = () => {
  const permissions = getStoreUser()?.permissions;
  return permissions && typeof permissions === 'object' ? permissions : null;
};

/*
 * Les helpers ci-dessous ne décident rien : le back est l'autorité et rend
 * ses décisions toutes faites dans `GET /api/user`. Le front ne fait que les
 * afficher. Synchrones (appelés en plein rendu) : tant que la réponse n'est
 * pas arrivée, ils rendent false.
 */

/**
 * Accès au web-editor (feature src/webviewer/).
 *   - `canUseWebEditor()` : sur l'ensemble des métiers (`permissions.web_editor`).
 *   - `canUseWebEditor(workName)` : sur ce métier seul
 *     (`works_details[].web_editor`) ; false pour un métier absent.
 */
export const canUseWebEditor = (workName) => {
  if (!workName) return getUserPermissions()?.web_editor === true;
  const worksDetails = getStoreUser()?.works_details;
  if (!Array.isArray(worksDetails)) return false;
  return worksDetails.find((work) => work?.name === workName)?.web_editor === true;
};

/** Valider un site web, éditer son squelette (`permissions.web_admin`). */
export const canAdministrate = () => getUserPermissions()?.web_admin === true;

/**
 * Modifier les « Règles générales » (écran /admin, PATCH /api/company_settings) :
 * `permissions.manage_general_rules`.
 */
export const canAdministrateGeneralRules = () =>
  getUserPermissions()?.manage_general_rules === true;

export { isAnUrl } from '../shared/variables/variableBoxValues';
